import { useState } from "react";
import { DateTime } from "luxon";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@somethings-ui/web";
import { ConfirmActionDialog } from "@/components/ui/confirm-action-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Button as DialogButton } from "@/components/ui/button";
import { useAbility } from "@/lib/casl/ability-context";
import { hasPrivilegedAccessFromAbility } from "@/lib/casl/privileged-access";
import {
  useUpdateUser,
  type LockHistoryEntry,
  type User,
} from "@/lib/api/user";
import { AccountField, AccountSectionCard } from "./account-field";

interface AccessControlSectionProps {
  user: User;
}

export function AccessControlSection({ user }: AccessControlSectionProps) {
  const { mutate: updateUser, isPending } = useUpdateUser();
  const ability = useAbility();
  const canUpdateUsers =
    hasPrivilegedAccessFromAbility(ability) && ability.can("update", "users");

  const [showLockDialog, setShowLockDialog] = useState(false);
  const [showUnlockDialog, setShowUnlockDialog] = useState(false);
  const [showDisableIntercomDialog, setShowDisableIntercomDialog] =
    useState(false);
  const [lockReason, setLockReason] = useState("");
  const [lockReasonPublic, setLockReasonPublic] = useState("");
  const [historyExpanded, setHistoryExpanded] = useState(false);

  const lockHistory = user.metadata?.lock_history ?? [];
  const lastLock = lockHistory.at(-1);
  const currentReason = user.locked && lastLock ? lastLock.locked_reason : null;
  const currentPublicReason =
    user.locked && lastLock ? lastLock.locked_reason_public : null;
  const intercomDisabled = user.metadata?.intercom_disabled === true;
  const actionsDisabled = !canUpdateUsers || isPending;

  const resetLockDialog = () => {
    setShowLockDialog(false);
    setLockReason("");
    setLockReasonPublic("");
  };

  const handleLock = () => {
    const trimmedPublicReason = lockReasonPublic.trim();
    updateUser(
      {
        userId: user.id,
        params: {
          locked: true,
          lockedReason: lockReason.trim(),
          lockedReasonPublic: trimmedPublicReason || null,
        },
      },
      { onSuccess: resetLockDialog },
    );
  };

  const handleUnlock = () => {
    updateUser(
      { userId: user.id, params: { locked: false } },
      { onSuccess: () => setShowUnlockDialog(false) },
    );
  };

  const handleToggleIntercom = (enabled: boolean) => {
    if (!canUpdateUsers) return;
    if (!enabled) {
      setShowDisableIntercomDialog(true);
      return;
    }
    updateUser({
      userId: user.id,
      params: { metadata: { intercom_disabled: false } },
    });
  };

  const handleConfirmDisableIntercom = () => {
    updateUser(
      {
        userId: user.id,
        params: { metadata: { intercom_disabled: true } },
      },
      { onSuccess: () => setShowDisableIntercomDialog(false) },
    );
  };

  return (
    <>
      <h2 className="mt-8 font-heading text-base font-bold leading-6 text-text-primary">
        Access Control
      </h2>
      <AccountSectionCard className="mt-6">
        <AccountField
          label="Account"
          value={user.locked ? "Locked" : "Unlocked"}
          open={false}
          onOpen={() => undefined}
          onCancel={() => undefined}
          readOnly
          valueContent={
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p
                  className={
                    user.locked
                      ? "font-body text-[15px] leading-[22.5px] text-states-error"
                      : "font-body text-[15px] leading-[22.5px] text-[#11884d]"
                  }
                >
                  {user.locked ? "Locked" : "Unlocked"}
                </p>
                {currentReason ? (
                  <p className="mt-1 truncate font-body text-xs text-[#6e6b67]">
                    Reason: {currentReason}
                  </p>
                ) : null}
                {currentPublicReason ? (
                  <p className="mt-0.5 truncate font-body text-xs text-[#6e6b67]">
                    Shown to teen: {currentPublicReason}
                  </p>
                ) : null}
              </div>
              <Button
                size="sm"
                fullWidth={false}
                className={
                  user.locked
                    ? "min-w-[120px]"
                    : "min-w-[120px] bg-states-error text-white"
                }
                disabled={actionsDisabled}
                loading={isPending}
                onPress={() =>
                  user.locked
                    ? setShowUnlockDialog(true)
                    : setShowLockDialog(true)
                }
              >
                {user.locked ? "Unlock" : "Lock"}
              </Button>
            </div>
          }
        />

        <AccountField
          label="Intercom"
          value={intercomDisabled ? "Disabled" : "Enabled"}
          open={false}
          onOpen={() => undefined}
          onCancel={() => undefined}
          readOnly
          valueContent={
            <div className="flex items-center justify-between gap-4">
              <p
                className={
                  intercomDisabled
                    ? "font-body text-[15px] leading-[22.5px] text-states-error"
                    : "font-body text-[15px] leading-[22.5px] text-[#11884d]"
                }
              >
                {intercomDisabled ? "Disabled" : "Enabled"}
              </p>
              <Switch
                checked={!intercomDisabled}
                onCheckedChange={handleToggleIntercom}
                disabled={actionsDisabled}
                aria-label="Intercom support"
              />
            </div>
          }
        />

        <AccountField
          label="Lock history"
          value={`${lockHistory.length}`}
          open={false}
          onOpen={() => undefined}
          onCancel={() => undefined}
          readOnly
          valueContent={
            <div>
              <button
                type="button"
                className="flex items-center gap-2 font-body text-[15px] leading-[22.5px] text-text-primary"
                onClick={() => setHistoryExpanded((open) => !open)}
              >
                {lockHistory.length}
                {historyExpanded ? (
                  <ChevronUp className="size-3.5" />
                ) : (
                  <ChevronDown className="size-3.5" />
                )}
              </button>
              {historyExpanded ? (
                <div className="mt-3">
                  {lockHistory.length === 0 ? (
                    <p className="font-body text-sm text-[#6e6b67]">
                      No lock history
                    </p>
                  ) : (
                    [...lockHistory]
                      .reverse()
                      .map((entry, index) => (
                        <LockHistoryItem
                          key={`${entry.locked_at}-${index}`}
                          entry={entry}
                          isLast={index === lockHistory.length - 1}
                        />
                      ))
                  )}
                </div>
              ) : null}
            </div>
          }
        />
      </AccountSectionCard>

      <Dialog
        open={showLockDialog}
        onOpenChange={(open) => {
          if (!open) resetLockDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Lock User Account</DialogTitle>
            <DialogDescription>
              This will prevent the teen from accessing the app. They will see a
              locked screen with support resources.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="record-lock-reason">
                Internal reason <span className="text-red-500">*</span>
              </Label>
              <p className="text-xs text-neutral-500">
                For internal records only. Not shown to the teen.
              </p>
              <Textarea
                id="record-lock-reason"
                placeholder="Enter a reason for locking this account..."
                value={lockReason}
                onChange={(event) => setLockReason(event.target.value)}
                className="h-24 resize-none"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="record-lock-reason-public">
                Public message (optional)
              </Label>
              <p className="text-xs text-neutral-500">
                Shown to the teen on the locked screen. Leave blank to show the
                default message.
              </p>
              <Textarea
                id="record-lock-reason-public"
                placeholder="e.g. Please contact support to restore access."
                value={lockReasonPublic}
                onChange={(event) => setLockReasonPublic(event.target.value)}
                className="h-20 resize-none"
                maxLength={280}
              />
            </div>
          </div>
          <DialogFooter>
            <DialogButton variant="outline" onClick={resetLockDialog}>
              Cancel
            </DialogButton>
            <DialogButton
              variant="destructive"
              onClick={handleLock}
              disabled={isPending || !lockReason.trim()}
            >
              Lock Account
            </DialogButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmActionDialog
        isOpen={showUnlockDialog}
        onClose={() => setShowUnlockDialog(false)}
        onConfirm={handleUnlock}
        title="Unlock Account"
        description="This will restore the teen's access to the app. Are you sure you want to unlock this account?"
        loading={isPending}
        confirmVariant="default"
        confirmText="Unlock"
      />

      <ConfirmActionDialog
        isOpen={showDisableIntercomDialog}
        onClose={() => setShowDisableIntercomDialog(false)}
        onConfirm={handleConfirmDisableIntercom}
        title="Disable Intercom Support"
        description="This will remove the teen's ability to contact support from the locked screen. Are you sure?"
        loading={isPending}
        confirmText="Disable"
      />
    </>
  );
}

function LockHistoryItem({
  entry,
  isLast,
}: {
  entry: LockHistoryEntry;
  isLast: boolean;
}) {
  const lockedAt = DateTime.fromISO(entry.locked_at).toFormat(
    "MMM d, yyyy 'at' h:mm a",
  );
  const unlockedAt = entry.unlocked_at
    ? DateTime.fromISO(entry.unlocked_at).toFormat("MMM d, yyyy 'at' h:mm a")
    : null;

  return (
    <div className="relative flex gap-3 pb-4">
      <div className="flex flex-col items-center">
        <div className="mt-1 size-2.5 shrink-0 rounded-full bg-states-error" />
        {isLast ? null : <div className="w-px flex-1 bg-[#dfddd8]" />}
      </div>
      <div className="min-w-0 flex-1 pb-1">
        <p className="font-body text-sm font-medium text-text-primary">
          {entry.locked_reason}
        </p>
        {entry.locked_reason_public ? (
          <p className="mt-0.5 font-body text-xs italic text-[#6e6b67]">
            Shown to teen: {entry.locked_reason_public}
          </p>
        ) : null}
        <p className="mt-0.5 font-body text-xs text-[#6e6b67]">
          Locked: {lockedAt}
        </p>
        {unlockedAt ? (
          <p className="font-body text-xs text-[#6e6b67]">
            Unlocked: {unlockedAt}
          </p>
        ) : (
          <p className="font-body text-xs font-medium text-states-error">
            Currently locked
          </p>
        )}
      </div>
    </div>
  );
}
